import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import BirthDetailsForm from './BirthDetailsForm'
import { savedToBirth } from './useSavedKundlis'

/** Short, user-facing copy per failure — never the raw API message. */
function errorCopy(err) {
  if (err?.status === 409) return 'You already have a saved kundli with these exact birth details.'
  if (err?.status === 422) return 'Some details look invalid — please check the date, time and place.'
  return 'We couldn’t save your changes right now. Please try again.'
}

/**
 * Edit a saved kundli — the studio's own birth form, prefilled, plus the
 * row's label & notes. Portalled to <body> at the dashboard modal layer
 * (z-1200) so no card's stacking context or the site header covers it; the
 * date, time and place pickers already portal above that.
 *
 * `onSave(birth, { label, notes })` must return a promise (the hook's
 * `updateBirth`); the dialog closes once it resolves.
 */
export default function EditKundliModal({ kundli, onSave, onClose }) {
  const [label, setLabel] = useState(kundli.label || '')
  const [notes, setNotes] = useState(kundli.notes || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  const submit = async (birth) => {
    if (!birth.location) {
      setError('Please search and pick the birth place from the list.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await onSave(birth, { label, notes })
      onClose()
    } catch (err) {
      console.warn('[kundli-studio] could not update kundli', err)
      setError(errorCopy(err))
    } finally {
      setBusy(false)
    }
  }

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className="kundli-studio ks-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div className="ks-modal" role="dialog" aria-modal="true" aria-labelledby="ks-edit-title">
        <div className="ks-modal-head">
          <div>
            <span className="ks-banner-eyebrow">Saved kundli</span>
            <h3 id="ks-edit-title" className="ks-modal-title">Edit {kundli.name || 'chart'}</h3>
          </div>
          <button type="button" className="ks-modal-close" onClick={onClose} disabled={busy} aria-label="Close">
            ✕
          </button>
        </div>

        <BirthDetailsForm initial={savedToBirth(kundli)} onGenerate={submit} busy={busy}>
          <div className="ks-edit-extra">
            <div>
              <label className="flbl" htmlFor="ks-edit-label">Label</label>
              <input
                id="ks-edit-label"
                className="field"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={100}
                placeholder="e.g. Mother, Business partner"
              />
            </div>
            <div className="ks-edit-notes">
              <label className="flbl" htmlFor="ks-edit-notes">Notes</label>
              <textarea
                id="ks-edit-notes"
                className="field"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
                placeholder="Anything to remember about this chart"
              />
            </div>
          </div>
          {error && <p className="ks-notice ks-edit-error" role="alert">{error}</p>}
        </BirthDetailsForm>
      </div>
    </div>,
    document.body,
  )
}
